import React, { useEffect, useState } from 'react';
import { Button, Form, Alert } from 'react-bootstrap';
import { useNavigate } from "react-router-dom";
import { backendURL } from '../IPaddress'; // Ensure this points to your backend server
import '../CSS/CRUDfiles.css'; // Adjust the path as necessary
import ErrorMessage from '../store/ErrorMessage';
import { useAuth } from '../store/AuthContext';
import 'bootstrap-icons/font/bootstrap-icons.css';
import {
  CrudListShell,
  ExpandableRow,
  FormGrid,
  FormActions,
  ScheduleEmptyState,
  ConfirmDeleteModal,
  FilterBar,
  SearchInput,
  SortControl,
  ResultSummary,
  useListControls,
} from '../components/crud';

const JUDGE_SEARCH_FIELDS = ['first_name', 'last_name', 'email'];
const JUDGE_SORT_OPTIONS = [
  { key: 'last_name', label: 'Last Name', accessor: (p) => p.last_name?.toLowerCase() ?? '' },
  { key: 'first_name', label: 'First Name', accessor: (p) => p.first_name?.toLowerCase() ?? '' },
  { key: 'email', label: 'Email', accessor: (p) => p.email?.toLowerCase() ?? '' },
];


function JudgeProfile() {
  const [profiles, setProfiles] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editProfileData, setEditProfileData] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [profileToDelete, setProfileToDelete] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const navigate = useNavigate();
  const {authState} =useAuth();

  const {
    items: visibleProfiles,
    resultCount,
    totalCount,
    isFiltered,
    search,
    sort,
    clear,
  } = useListControls(profiles, {
    searchFields: JUDGE_SEARCH_FIELDS,
    sortOptions: JUDGE_SORT_OPTIONS,
  });



  useEffect(() => {
    if (!authState) {
        navigate('/login');
    }
    // Dependency array includes authState to react to its changes
}, [authState, navigate]);


  // Fetch Profiles
  useEffect(() => {
    fetchProfiles();
  }, []);

  const fetchProfiles = async () => {
    try {
      const response = await fetch(`${backendURL}/api/profiles`, {
        method: 'GET',
        credentials: 'include', // Make sure to send credentials if your backend requires authentication
      });
      if (!response.ok) throw new Error('Failed to fetch profiles');
      const data = await response.json();
      const judgesFiltered = data.filter(profile => profile.judge_status === true);

      setProfiles(judgesFiltered);
    } catch (error) {
      setErrorMessage('Error fetching profiles here');
    }
  };

  const handleShowFormToggle = () => {
    if (!showForm) { // Reset form only when opening the form
      resetForm();
    }
    setShowForm(!showForm);
  };
  const resetForm = () => {
    setEditProfileData(null); // Clear any existing edit data
    setErrorMessage(''); // Clear any error messages
  };

  const handleEditProfile = (profile) => {
    setEditProfileData(profile);
    setShowForm(true);
  };

  const handleDeleteConfirmation = (profileId) => {
    setProfileToDelete(profileId);
    setShowModal(true);
  };

  const handleDeleteProfile = async () => {
    try {
      const response = await fetch(`${backendURL}/api/profiles/${profileToDelete}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Failed to delete profile');
      setShowModal(false);
      fetchProfiles(); // Refresh the list after deletion
    } catch (error) {
      console.error("Error deleting profile:", error);
      setErrorMessage('Check to see if this judge is associated with any programs. Remove them, and then try deleting again.');
    }
  };


function ProfileForm({ editData, setErrorMessage, setShowForm, fetchProfiles }) {
  const [formData, setFormData] = useState({
    first_name: null,
    last_name: null,
    email: null,
    secondary_email: null,
    // cell_phone_number: null,
    // home_phone_number: null,
    street: null,
    city: null,
    state: null,
    zip: null,
    teacher_status: null,
    judge_status: true,
  });

  useEffect(() => {
    if (editData) {
      setFormData({
        first_name: editData.first_name || null,
        last_name: editData.last_name || null,
        email: editData.email || null,
        secondary_email: editData.secondary_email || null,
        // cell_phone_number: editData.cell_phone_number || null,
        // home_phone_number: editData.home_phone_number || null,
        street: editData.street || null,
        city: editData.city || null,
        state: editData.state || null,
        zip: editData.zip || null,
        teacher_status_id: editData.teacher_status_id || null,
        teacher_status: editData.teacher_status || null,
        judge_status: editData.judge_status || null,
      });
    }
  }, [editData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const endpoint = editData ? `${backendURL}/api/profiles/${editData.profile_id}` : `${backendURL}/api/profiles`;
    const method = editData ? 'PUT' : 'POST';

    try {
      console.log("Form Data:", formData)
      const response = await fetch(endpoint, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to submit the form');
      }

      fetchProfiles(); // Refresh the profiles list
      setShowForm(false); // Close the form
      setErrorMessage(''); // Clear any previous error messages
    } catch (error) {
      console.error("Form submission error:", error);
      setErrorMessage(error.message || 'Error submitting form');
    }
  };

  const handleCancel = () => {
    setFormData({ // Reset form data to initial state
      first_name: null,
      last_name: null,
      email: null,
      secondary_email: null,
      // cell_phone_number: null,
      // home_phone_number: null,
      street: null,
      city: null,
      state: null,
      zip: null,
      teacher_status_id: null,
      teacher_status: null,
      judge_status: null,
    });
    setErrorMessage(''); // Clear any error messages
    setShowForm(false); // Close the form
  };

  return (
    <Form onSubmit={handleSubmit}>
      <FormGrid>
        <Form.Group className="bold-label" controlId="first_name">
          <Form.Label>First Name</Form.Label>
          <Form.Control
            type="text"
            placeholder="Enter first name"
            name="first_name"
            value={formData.first_name ?? ""}
            onChange={handleChange}
            required
          />
        </Form.Group>

        <Form.Group className="bold-label" controlId="last_name">
          <Form.Label>Last Name</Form.Label>
          <Form.Control
            type="text"
            placeholder="Enter last name"
            name="last_name"
            value={formData.last_name ?? ""}
            onChange={handleChange}
            required
          />
        </Form.Group>

        <Form.Group className="bold-label" controlId="email">
          <Form.Label>Email</Form.Label>
          <Form.Control
            type="email"
            placeholder="Enter email"
            name="email"
            value={formData.email ?? ""}
            onChange={handleChange}
          />
        </Form.Group>
        <Form.Group className="bold-label" controlId="secondary_email">
          <Form.Label>Secondary Email</Form.Label>
          <Form.Control
            type="email"
            placeholder="Enter secondary email"
            name="secondary_email"
            value={formData.secondary_email ?? ""}
            onChange={handleChange}
          />
        </Form.Group>

        <FormActions>
          <Button variant="primary" type="submit">
            Submit
          </Button>
          <Button variant="secondary" onClick={handleCancel}>
            Cancel
          </Button>
        </FormActions>
      </FormGrid>
    </Form>
  );
}


  return (
    <div className="profile-management">
      <CrudListShell
        addLabel="Add New Profile"
        listLabel="Close Form"
        showForm={showForm}
        onToggleForm={handleShowFormToggle}
        tableHeading="Judges"
        filters={(
          <FilterBar
            summary={(
              <ResultSummary
                count={resultCount}
                total={totalCount}
                noun="judge"
                onClear={clear}
                isFiltered={isFiltered}
              />
            )}
          >
            <SearchInput label="Search judges" placeholder="Name or email" {...search} />
            <SortControl {...sort} />
          </FilterBar>
        )}
        form={(
          <ProfileForm
            editData={editProfileData}
            setErrorMessage={setErrorMessage}
            setShowForm={setShowForm}
            fetchProfiles={fetchProfiles}
          />
        )}
        emptyState={(
          <tr>
            <td>
              <ScheduleEmptyState>No judge profiles have been added yet.</ScheduleEmptyState>
            </td>
          </tr>
        )}
      >
        {visibleProfiles.map((profile) => (
          <ExpandableRow
            key={profile.profile_id}
            main={{
              title: `${profile.first_name} ${profile.last_name}`,
              subtitle: profile.email || 'No email on file',
            }}
            pills={profile.secondary_email ? [{ label: profile.secondary_email }] : []}
            actions={(
              <>
                <Button size="sm" onClick={() => handleEditProfile(profile)} aria-label="Edit profile">
                  <i className="bi bi-pencil"></i>
                </Button>
                <Button
                  size="sm"
                  variant="danger"
                  onClick={() => handleDeleteConfirmation(profile.profile_id)}
                  aria-label="Delete profile"
                >
                  <i className="bi bi-trash3"></i>
                </Button>
              </>
            )}
          />
        ))}
      </CrudListShell>

      <ConfirmDeleteModal
        show={showModal}
        onHide={() => setShowModal(false)}
        onConfirm={handleDeleteProfile}
        title="Delete Profile"
        bodyText="Are you sure you want to delete this profile?"
      />

      {errorMessage && <Alert variant="danger">{errorMessage}</Alert>}
      <ErrorMessage errorMessage={errorMessage} clearErrorMessage={() => setErrorMessage()} />
    </div>
  );
}

export default JudgeProfile;
